/**
 * taskDisplayName.ts
 * Collapse a redundant generated task name for display.
 *
 * Task names are built as `${lineDescription} — ${taskTypeName}` at generation, so
 * when the line description equals its task-type name you get "X — X", and the row
 * also shows the type as a subtitle → a redundant triplet. This drops a trailing
 * " — {type}" (or collapses an "A — A") so the bold line is just the distinctive
 * part. Shared by Phase 1 (task table) and Phase 2 (weekly grid) so they dedup
 * identically.
 */

/** Return the de-duplicated bold display name for a task. */
export function displayTaskName(name: string, typeName: string | null): string {
  if (!name) return name;
  if (typeName) {
    if (name === typeName) return name;
    const suffix = ` — ${typeName}`;
    if (name.endsWith(suffix)) {
      const head = name.slice(0, -suffix.length).trim();
      if (head) return head;
    }
  }
  const parts = name.split(' — ');
  if (parts.length === 2 && parts[0].trim() === parts[1].trim()) return parts[0].trim();
  return name;
}

/** Whether the task-type subtitle adds anything the bold name doesn't already show. */
export function showTypeSubtitle(name: string, typeName: string | null): boolean {
  return Boolean(typeName) && typeName !== displayTaskName(name, typeName);
}
